import { Trans } from '@lingui/react/macro'
import { LucideIcon } from 'components/LucideIcon'
import { Button } from 'designSystem/components/Button'
import { ScrollFade } from 'designSystem/components/ScrollFade'
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  useSidebar,
} from 'designSystem/components/Sidebar'
import { Text } from 'designSystem/components/Text'
import { useAgenticChatHistoryActions } from 'features/embeddedSidebar/hooks/useAgenticChatHistoryActions'
import { type RecordedAgenticChatSession } from 'features/embeddedSidebar/hooks/useAgenticChatSessionHistory'
import { useChatHistoryBulkActions } from 'features/embeddedSidebar/hooks/useChatHistoryBulkActions'
import type {
  DocChatConversation,
  DocumentTab,
} from 'features/knowledgeDocumentViewer/types'
import { getAgenticConversationKey } from 'features/knowledgeDocumentViewer/utils/agenticConversationIdentity'
import { useWorkspaceTabs } from 'features/workspaceTabs/contexts/WorkspaceTabsContext'
import { useMemo, useState } from 'react'

import { AgenticChatHistorySessionRow } from './AgenticChatHistorySessionRow'
import {
  ArchivedSection,
  ChatsPane,
  HistoryLabelGroup,
  HistoryState,
  HistoryToggleButton,
  HistoryToggleChevron,
  StickyChatChrome,
} from './agenticChatHistorySidebarStyles'
import { ChatHistoryOverflowMenu } from './ChatHistoryOverflowMenu'
import { useUnseenAgenticChatCompletions } from './useUnseenAgenticChatCompletions'

function openTabForSession({
  openConversationTabs,
  openTabs,
  session,
}: {
  openConversationTabs: ReadonlyMap<string, DocumentTab>
  openTabs: readonly DocumentTab[]
  session: RecordedAgenticChatSession
}): DocumentTab | undefined {
  return (
    openTabs.find((tab) => tab.id === session.sourceTabId) ??
    openConversationTabs.get(session.key)
  )
}

interface AgenticChatHistorySidebarProps {
  archivedHasMore?: boolean
  archivedSessions?: RecordedAgenticChatSession[]
  hasArchivedSessions?: boolean
  hasExpandedArchived?: boolean
  hydrateSession: (
    session: RecordedAgenticChatSession
  ) => Promise<DocChatConversation | null>
  isLoadingActive?: boolean
  isLoadingArchived?: boolean
  loadFailed?: boolean
  onArchiveSession: (sessionKey: string) => Promise<boolean>
  onArchiveSessions: (
    sessions: RecordedAgenticChatSession[]
  ) => Promise<boolean>
  onDeleteSession: (sessionKey: string) => Promise<boolean>
  onDeleteSessions: (sessionKeys: string[]) => Promise<boolean>
  onLoadMoreArchived?: () => void
  onPinSession: (sessionKey: string, pinned: boolean) => void
  onRemoveLocalSession: (sessionKey: string) => void
  onRenameSession: (sessionKey: string, title: string) => void
  onRetryActiveLoad?: () => void
  onRetrySession?: (session: RecordedAgenticChatSession) => void
  onToggleArchived?: () => void
  onUnarchiveSession: (sessionKey: string) => Promise<boolean>
  onUnarchiveSessions: (
    sessions: RecordedAgenticChatSession[]
  ) => Promise<boolean>
  sessions: RecordedAgenticChatSession[]
  siteId: string | undefined
}

export function AgenticChatHistorySidebar({
  archivedHasMore = false,
  archivedSessions = [],
  hasArchivedSessions = archivedSessions.length > 0,
  hasExpandedArchived = false,
  hydrateSession,
  isLoadingActive = false,
  isLoadingArchived = false,
  loadFailed = false,
  onArchiveSession,
  onArchiveSessions,
  onDeleteSession,
  onDeleteSessions,
  onLoadMoreArchived,
  onPinSession,
  onRemoveLocalSession,
  onRenameSession,
  onRetryActiveLoad,
  onRetrySession,
  onToggleArchived,
  onUnarchiveSession,
  onUnarchiveSessions,
  sessions,
  siteId,
}: AgenticChatHistorySidebarProps) {
  const { activeTabId, openTabs } = useWorkspaceTabs()
  const { state: sidebarState } = useSidebar()
  const isCollapsed = sidebarState === 'collapsed'
  const [isHistoryOpen, setIsHistoryOpen] = useState(true)
  const { archiveSession, deleteSession, renameSession, selectSession } =
    useAgenticChatHistoryActions({
      hydrateSession,
      onArchiveSession,
      onDeleteSession,
      onRemoveLocalSession,
      onRenameSession,
      siteId,
    })

  const openConversationTabs = useMemo(() => {
    const tabsByConversationKey = new Map<string, DocumentTab>()
    openTabs.forEach((tab) => {
      const conversationKey = getAgenticConversationKey(tab)
      if (conversationKey) tabsByConversationKey.set(conversationKey, tab)
    })
    return tabsByConversationKey
  }, [openTabs])

  const historySessions = useMemo(
    () => [...sessions, ...archivedSessions],
    [archivedSessions, sessions]
  )
  const handleBulkAction = useChatHistoryBulkActions({
    onArchiveSessions,
    onDeleteSessions,
    onUnarchiveSessions,
    onRemoveLocalSession,
    sessions: historySessions,
    siteId,
  })
  const activeSessionKey = useMemo(() => {
    const activeSession = historySessions.find((session) => {
      const openTab = openTabForSession({
        openConversationTabs,
        openTabs,
        session,
      })
      return openTab !== undefined && openTab.id === activeTabId
    })
    return activeSession?.key
  }, [activeTabId, historySessions, openConversationTabs, openTabs])
  const unseenCompletedKeys = useUnseenAgenticChatCompletions({
    activeSessionKey,
    sessions: historySessions,
  })

  const showHistoryPane =
    !loadFailed &&
    (sessions.length > 0 || (siteId !== undefined && !isCollapsed))
  const showHistoryBody = isHistoryOpen || isCollapsed
  const showArchivedSection =
    hasArchivedSessions &&
    (!hasExpandedArchived ||
      isLoadingArchived ||
      archivedHasMore ||
      archivedSessions.length > 0)

  return (
    <ChatsPane>
      <StickyChatChrome>
        <HistoryLabelGroup>
          <SidebarGroupLabel asChild>
            <HistoryToggleButton
              aria-expanded={isHistoryOpen}
              type='button'
              onClick={() => setIsHistoryOpen((current) => !current)}>
              <Trans>Recent chats</Trans>
              <HistoryToggleChevron>
                <LucideIcon
                  aria-hidden='true'
                  name={isHistoryOpen ? 'ChevronDown' : 'ChevronRight'}
                  size={16}
                />
              </HistoryToggleChevron>
            </HistoryToggleButton>
          </SidebarGroupLabel>
          {siteId && !isCollapsed ? (
            <ChatHistoryOverflowMenu
              key={`${siteId}:recent`}
              section='recent'
              disabled={loadFailed || isLoadingActive || sessions.length === 0}
              onAction={handleBulkAction}
            />
          ) : null}
        </HistoryLabelGroup>
      </StickyChatChrome>
      {showHistoryBody ? (
        <>
          {loadFailed && !isCollapsed ? (
            <HistoryState>
              <Text variant='caption' color='muted' align='center'>
                <Trans>Could not load chats.</Trans>
              </Text>
              {onRetryActiveLoad ? (
                <Button
                  size='sm'
                  type='button'
                  variant='ghost'
                  onClick={onRetryActiveLoad}>
                  <Trans>Retry</Trans>
                </Button>
              ) : null}
            </HistoryState>
          ) : null}
          {showHistoryPane ? (
            <ScrollFade
              fadeEdges='both'
              scrollbar={isCollapsed ? 'hidden' : 'thin'}>
              {sessions.length > 0 ? (
                <SidebarGroup>
                  <SidebarMenu>
                    {sessions.map((session) => {
                      const openTab = openTabForSession({
                        openConversationTabs,
                        openTabs,
                        session,
                      })
                      return (
                        <AgenticChatHistorySessionRow
                          key={session.key}
                          hasUnseenAnswer={unseenCompletedKeys.has(session.key)}
                          isActive={
                            openTab !== undefined && openTab.id === activeTabId
                          }
                          onArchive={archiveSession}
                          onDelete={deleteSession}
                          onPin={onPinSession}
                          onRename={renameSession}
                          onRetry={onRetrySession}
                          onSelect={selectSession}
                          openTabId={openTab?.id}
                          session={session}
                        />
                      )
                    })}
                  </SidebarMenu>
                </SidebarGroup>
              ) : isLoadingActive && !isCollapsed ? (
                <HistoryState>
                  <Text variant='caption' color='muted' align='center'>
                    <Trans>Loading chats…</Trans>
                  </Text>
                </HistoryState>
              ) : null}
            </ScrollFade>
          ) : isCollapsed || loadFailed ? null : (
            <HistoryState>
              <Text variant='caption' color='muted' align='center'>
                <Trans>Select a site to view chats.</Trans>
              </Text>
            </HistoryState>
          )}
        </>
      ) : null}
      {siteId && !isCollapsed && showArchivedSection ? (
        <ArchivedSection>
          <HistoryLabelGroup>
            <SidebarGroupLabel asChild>
              <HistoryToggleButton
                aria-expanded={hasExpandedArchived}
                type='button'
                onClick={onToggleArchived}>
                <Trans>Archived chats</Trans>
                <HistoryToggleChevron>
                  <LucideIcon
                    aria-hidden='true'
                    name={hasExpandedArchived ? 'ChevronDown' : 'ChevronRight'}
                    size={16}
                  />
                </HistoryToggleChevron>
              </HistoryToggleButton>
            </SidebarGroupLabel>
            <ChatHistoryOverflowMenu
              key={`${siteId}:archived`}
              section='archived'
              disabled={isLoadingArchived}
              onAction={handleBulkAction}
            />
          </HistoryLabelGroup>
          {hasExpandedArchived ? (
            <ScrollFade fadeEdges='both' scrollbar='thin'>
              <SidebarGroup>
                <SidebarMenu>
                  {archivedSessions.map((session) => {
                    const openTab = openTabForSession({
                      openConversationTabs,
                      openTabs,
                      session,
                    })
                    return (
                      <AgenticChatHistorySessionRow
                        key={session.key}
                        hasUnseenAnswer={unseenCompletedKeys.has(session.key)}
                        isActive={
                          openTab !== undefined && openTab.id === activeTabId
                        }
                        isArchived
                        onArchive={archiveSession}
                        onDelete={deleteSession}
                        onRename={renameSession}
                        onRetry={onRetrySession}
                        onSelect={selectSession}
                        onUnarchive={onUnarchiveSession}
                        openTabId={openTab?.id}
                        session={session}
                      />
                    )
                  })}
                </SidebarMenu>
                {isLoadingArchived ? (
                  <HistoryState>
                    <Text variant='caption' color='muted' align='center'>
                      <Trans>Loading archived chats…</Trans>
                    </Text>
                  </HistoryState>
                ) : null}
                {archivedHasMore ? (
                  <Button
                    size='sm'
                    type='button'
                    variant='ghost'
                    width='full'
                    onClick={onLoadMoreArchived}>
                    <Trans>Load more</Trans>
                  </Button>
                ) : null}
              </SidebarGroup>
            </ScrollFade>
          ) : null}
        </ArchivedSection>
      ) : null}
    </ChatsPane>
  )
}
